import { CONTEST_STATUSES, formatDate, type ContestStatus } from '@hr/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { BriefcaseIcon, FileSpreadsheetIcon, PlusIcon } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';
import { Combobox } from '@/components/combobox';
import { DataTable } from '@/components/data-table';
import { DateInput } from '@/components/date-input';
import { EmptyState, PageHeader } from '@/components/page';
import { SearchInput } from '@/components/search-input';
import { StageCounts } from '@/components/stage';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ContestFormSheet } from '@/features/contests/contest-form-sheet';
import { STATUS_VARIANT } from '@/features/contests/status';
import { usePreview } from '@/features/preview/use-preview';
import { CONTEST_STATUS_LABELS, uk } from '@/i18n/uk';
import { useReferences } from '@/lib/references';
import { listSearchShape, tableState, tableStateToSearch } from '@/lib/search-params';
import { trpc } from '@/lib/trpc';
import { isHr } from '@/lib/roles';
import type { ContestListItem } from '@/lib/types';

const searchSchema = z.object({
  ...listSearchShape,
  status: z.enum(CONTEST_STATUSES).optional().catch(undefined),
  location: z.uuid().optional().catch(undefined),
  position: z.uuid().optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  new: z.boolean().optional().catch(undefined),
  /** Новий конкурс на штатну одиницю (з картки штатної одиниці). */
  staffPosition: z.uuid().optional().catch(undefined),
});

/** Експорт у Excel з тими самими фільтрами, що й таблиця. */
function exportUrl(search: z.infer<typeof searchSchema>) {
  const params = new URLSearchParams();
  if (search.q) params.set('q', search.q);
  if (search.status) params.set('status', search.status);
  if (search.location) params.set('locationId', search.location);
  if (search.position) params.set('positionId', search.position);
  if (search.from) params.set('from', search.from);
  if (search.to) params.set('to', search.to);
  const qs = params.toString();
  return `/api/contests/export.xlsx${qs ? `?${qs}` : ''}`;
}

export const Route = createFileRoute('/_app/contests/')({
  validateSearch: searchSchema,
  component: ContestsPage,
});

function ContestsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { me } = Route.useRouteContext();
  const refs = useReferences();
  const state = tableState(search, { sort: 'announcedAt', dir: 'desc' });
  const canWrite = isHr(me.role);
  const openPreview = usePreview();

  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const list = useQuery({
    ...trpc.contests.list.queryOptions({
      page: state.page,
      pageSize: state.pageSize,
      sort: state.sort,
      dir: state.dir,
      q: search.q,
      status: search.status ? [search.status] : undefined,
      locationId: search.location,
      positionId: search.position,
      from: search.from,
      to: search.to,
    }),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<ColumnDef<ContestListItem>[]>(
    () => [
      {
        id: 'title',
        header: uk.contests.titleField,
        cell: ({ row }) => <span className="font-medium">{row.original.title}</span>,
        meta: { className: 'min-w-64' },
      },
      { id: 'position', header: uk.contests.position, accessorKey: 'positionTitle' },
      { id: 'location', header: uk.contests.location, accessorKey: 'locationName' },
      { id: 'projectCode', header: uk.contests.project, accessorKey: 'projectCode' },
      {
        id: 'announcedAt',
        header: uk.contests.announcedAt,
        cell: ({ row }) => formatDate(row.original.announcedAt),
        meta: { className: 'whitespace-nowrap' },
      },
      {
        id: 'endsAt',
        header: uk.contests.endsAt,
        cell: ({ row }) => formatDate(row.original.endsAt),
        meta: { className: 'whitespace-nowrap' },
      },
      {
        id: 'closedEarlyAt',
        header: uk.contests.closedEarlyAt,
        enableSorting: false,
        cell: ({ row }) => formatDate(row.original.closedEarlyAt),
        meta: { className: 'whitespace-nowrap' },
      },
      {
        id: 'stages',
        header: uk.contests.candidates,
        enableSorting: false,
        cell: ({ row }) => <StageCounts counts={row.original.stageCounts} />,
      },
      {
        id: 'status',
        header: uk.contests.status,
        cell: ({ row }) => (
          <Badge variant="outline" className={STATUS_VARIANT[row.original.status]}>
            {CONTEST_STATUS_LABELS[row.original.status]}
          </Badge>
        ),
      },
    ],
    [],
  );

  const hasFilters = Boolean(
    search.q || search.status || search.location || search.position || search.from || search.to,
  );

  return (
    <>
      <PageHeader
        title={uk.contests.title}
        actions={
          <>
            <Button variant="outline" onClick={() => openPreview({ url: exportUrl(search) })}>
              <FileSpreadsheetIcon /> {uk.contests.exportList}
            </Button>
            {canWrite && (
              <Button onClick={() => setSearch({ new: true })}>
                <PlusIcon /> {uk.contests.new}
              </Button>
            )}
          </>
        }
      />

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <SearchInput
          value={search.q ?? ''}
          onChange={(q) => setSearch({ q: q || undefined, page: undefined })}
        />
        <Select
          value={search.status ?? 'all'}
          onValueChange={(v) =>
            setSearch({ status: v === 'all' ? undefined : (v as ContestStatus), page: undefined })
          }
        >
          <SelectTrigger className="w-40" aria-label={uk.contests.status}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {uk.contests.status}: {uk.common.all.toLowerCase()}
            </SelectItem>
            {CONTEST_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {CONTEST_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Combobox
          className="w-44"
          value={search.location}
          onChange={(v) => setSearch({ location: v ?? undefined, page: undefined })}
          options={(refs.data?.locations ?? []).map((l) => ({ value: l.id, label: l.name }))}
          placeholder={uk.contests.location}
          allowClear
        />
        <Combobox
          className="w-56"
          value={search.position}
          onChange={(v) => setSearch({ position: v ?? undefined, page: undefined })}
          options={(refs.data?.positions ?? []).map((p) => ({ value: p.id, label: p.title }))}
          placeholder={uk.contests.position}
          allowClear
        />
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <span>{uk.contests.from}</span>
          <DateInput
            className="w-36"
            value={search.from}
            onChange={(v) => setSearch({ from: v ?? undefined, page: undefined })}
          />
          <span>{uk.contests.to}</span>
          <DateInput
            className="w-36"
            value={search.to}
            onChange={(v) => setSearch({ to: v ?? undefined, page: undefined })}
          />
        </div>
        {hasFilters && (
          <Button
            variant="ghost"
            onClick={() =>
              setSearch({
                q: undefined,
                status: undefined,
                location: undefined,
                position: undefined,
                from: undefined,
                to: undefined,
                page: undefined,
              })
            }
          >
            {uk.contests.resetFilters}
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={list.data?.items}
        total={list.data?.total ?? 0}
        state={state}
        onStateChange={(next) => setSearch(tableStateToSearch(next))}
        isLoading={list.isPending}
        getRowId={(r) => r.id}
        onRowClick={(r) =>
          void navigate({ to: '/contests/$contestId', params: { contestId: r.id } })
        }
        empty={
          hasFilters ? (
            <EmptyState icon={BriefcaseIcon} title={uk.contests.emptyFiltered} />
          ) : (
            <EmptyState
              icon={BriefcaseIcon}
              title={uk.contests.empty}
              hint={uk.contests.emptyHint}
              action={
                canWrite && (
                  <Button onClick={() => setSearch({ new: true })}>
                    <PlusIcon /> {uk.contests.new}
                  </Button>
                )
              }
            />
          )
        }
      />

      <ContestFormSheet
        open={Boolean(search.new)}
        staffPositionId={search.staffPosition}
        onOpenChange={(o) => setSearch({ new: o || undefined, staffPosition: undefined })}
      />
    </>
  );
}
